import { useState } from "react";
import { CancellationDetails, Reason } from "../types";

interface ModalState {
  summary: {
    active: boolean;
    open: () => void;
    close: () => void;
  };
  confirmClose: {
    active: boolean;
    open: () => void;
    close: () => void;
  };
  treatment: {
    active: boolean;
    title: string;
    content: string;
    pendingReason?: Reason;
    open: (reason: Reason) => void;
    close: () => void;
  };
  cancelBooking: {
    active: boolean;
    open: () => void;
    close: () => void;
  };
  cancelSuccess: {
    active: boolean;
    details: CancellationDetails | null;
    open: (details: CancellationDetails) => void;
    close: () => void;
  };
  cancelFailure: {
    active: boolean;
    details: CancellationDetails | null;
    open: (details: CancellationDetails) => void;
    close: () => void;
  };
}

/**
 * Modal state management hook for Provet booking form.
 *
 * Centralizes all modal visibility states and provides consistent open/close APIs
 * for 6 different modals used throughout the booking flow.
 *
 * **Modals Managed:**
 *
 * 1. **Summary Modal** - Final review before booking submission
 *    - Shows all selected information
 *    - Allows editing of previous steps
 *    - Contains "Confirm Booking" button
 *
 * 2. **Confirm Close Modal** - Warns before closing summary
 *    - Prevents accidental loss of booking progress
 *    - Shown when user clicks X on summary modal
 *
 * 3. **Treatment Modal** - Shows treatment/reason instructions
 *    - Displays instructions for selected treatment
 *    - HTML content with formatting
 *    - Stores pendingReason until confirmed
 *
 * 4. **Cancel Booking Modal** - Confirms booking cancellation
 *    - Shown on confirmation page
 *    - Requires explicit confirmation
 *
 * 5. **Cancel Success Modal** - Feedback after successful cancellation
 *    - Shows appointment details that were cancelled
 *    - Provides confirmation message
 *
 * 6. **Cancel Failure Modal** - Feedback after failed cancellation
 *    - Shows error information
 *    - Provides clinic contact details for manual cancellation
 *
 * **State Structure:**
 * Each modal has:
 * - `active: boolean` - Visibility state
 * - `open()` - Function to show modal
 * - `close()` - Function to hide modal
 * - Additional properties for modal-specific data (title, content, details, etc.)
 *
 * **Usage Pattern:**
 * ```tsx
 * const modals = useProvetModals();
 *
 * // Open summary modal
 * modals.summary.open();
 *
 * // Open treatment modal with content
 * modals.treatment.open(selectedReason);
 *
 * // Check if modal is active
 * if (modals.summary.active) {
 *   // Modal is visible
 * }
 *
 * // Close modal
 * modals.summary.close();
 *
 * // Access modal content
 * const treatmentTitle = modals.treatment.title;
 * const treatmentContent = modals.treatment.content;
 * ```
 *
 * @returns Object with state and controls for all modals
 *
 * @example
 * ```tsx
 * function BookingForm() {
 *   const modals = useProvetModals();
 *
 *   const handleReasonSelect = (reason: Reason) => {
 *     modals.treatment.open(reason);
 *   };
 *
 *   const handleConfirmTreatment = () => {
 *     const reason = modals.treatment.pendingReason;
 *     // Process reason...
 *     modals.treatment.close();
 *   };
 *
 *   return (
 *     <>
 *       <TreatmentModal
 *         active={modals.treatment.active}
 *         title={modals.treatment.title}
 *         content={modals.treatment.content}
 *         onConfirm={handleConfirmTreatment}
 *         onCancel={modals.treatment.close}
 *       />
 *       <SummaryModal
 *         active={modals.summary.active}
 *         onClose={modals.summary.close}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export const useProvetModals = () => {
  // Summary modal state
  const [summaryModalActive, setSummaryModalActive] = useState<boolean>(false);

  // Confirm close modal state
  const [confirmCloseSummaryModalActive, setConfirmCloseSummaryModalActive] =
    useState<boolean>(false);

  // Treatment modal state
  const [treatmentModalActive, setTreatmentModalActive] = useState<boolean>(false);
  const [treatmentModalTitle, setTreatmentModalTitle] = useState<string>("");
  const [treatmentModalText, setTreatmentModalText] = useState<string>("");
  const [pendingReason, setPendingReason] = useState<Reason | undefined>();

  // Cancel booking modal state
  const [cancelBookingModalActive, setCancelBookingModalActive] =
    useState<boolean>(false);

  // Cancel success modal state
  const [cancelSuccessModalActive, setCancelSuccessModalActive] =
    useState<boolean>(false);
  const [cancelSuccessDetails, setCancelSuccessDetails] =
    useState<CancellationDetails | null>(null);

  // Cancel failure modal state
  const [cancelFailureModalActive, setCancelFailureModalActive] =
    useState<boolean>(false);
  const [cancelFailureDetails, setCancelFailureDetails] =
    useState<CancellationDetails | null>(null);

  const modalState: ModalState = {
    summary: {
      active: summaryModalActive,
      open: () => setSummaryModalActive(true),
      close: () => setSummaryModalActive(false)
    },
    confirmClose: {
      active: confirmCloseSummaryModalActive,
      open: () => setConfirmCloseSummaryModalActive(true),
      close: () => setConfirmCloseSummaryModalActive(false)
    },
    treatment: {
      active: treatmentModalActive,
      title: treatmentModalTitle,
      content: treatmentModalText,
      pendingReason,
      open: (reason: Reason) => {
        setTreatmentModalTitle(reason.name || "");
        setTreatmentModalText(reason.instructions?.replace(/\n/g, "<br />") || "");
        setPendingReason(reason);
        setTreatmentModalActive(true);
      },
      close: () => {
        setTreatmentModalActive(false);
        setPendingReason(undefined);
      }
    },
    cancelBooking: {
      active: cancelBookingModalActive,
      open: () => setCancelBookingModalActive(true),
      close: () => setCancelBookingModalActive(false)
    },
    cancelSuccess: {
      active: cancelSuccessModalActive,
      details: cancelSuccessDetails,
      open: (details: CancellationDetails) => {
        setCancelSuccessDetails(details);
        setCancelSuccessModalActive(true);
      },
      close: () => {
        setCancelSuccessModalActive(false);
        setCancelSuccessDetails(null);
      }
    },
    cancelFailure: {
      active: cancelFailureModalActive,
      details: cancelFailureDetails,
      open: (details: CancellationDetails) => {
        setCancelFailureDetails(details);
        setCancelFailureModalActive(true);
      },
      close: () => {
        setCancelFailureModalActive(false);
        setCancelFailureDetails(null);
      }
    }
  };

  return modalState;
};
